import React from "react";
import { Check, X, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";

interface FeatureGroup {
  groupName: string;
  features: FeatureRow[];
}

interface FeatureRow {
  title: string;
  description: string;
  tooltip: string;
  comingSoon?: boolean;
  free: {
    available: boolean;
    creditCost?: number | string;
    limitation?: string;
  };
  weekly: {
    available: boolean;
    creditCost?: number | string;
    limitation?: string;
  };
  soloAndPro: {
    available: boolean;
    creditCost?: number | string;
    limitation?: string;
  };
}

const featureGroups: FeatureGroup[] = [
  {
    groupName: "Core Features",
    features: [
      {
        title: "Chat",
        description: "Ask Arcitext anything about your writing",
        tooltip: "Arcitext Chat helps you research, optimize, and refine your texts while understanding your writing context and goals.",
        free: {
          available: true,
          creditCost: "Variable",
          limitation: "Full access",
        },
        weekly: {
          available: true,
          creditCost: "Variable",
          limitation: "Full access",
        },
        soloAndPro: {
          available: true,
          creditCost: "Variable",
          limitation: "Full access",
        },
      },
      {
        title: "Text Editor",
        description: "Includes focus mode, word count, exporting, and more",
        tooltip: "The bread and butter of Arcitext. Write, edit, and improve your text with on-demand guidance and suggestions from Arcitext.",
        free: {
          available: true,
          creditCost: "Free",
          limitation: "Full access",
        },
        weekly: {
          available: true,
          creditCost: "Free",
          limitation: "Full access",
        },
        soloAndPro: {
          available: true,
          creditCost: "Free",
          limitation: "Full access",
        },
      },
      {
        title: "Content Repository",
        description: "Organize and manage all your content",
        tooltip: "Save drafts, tag content, filter by status, and manage your content library",
        free: {
          available: true,
          creditCost: "Free",
          limitation: "10 documents",
        },
        weekly: {
          available: true,
          creditCost: "Free",
          limitation: "Unlimited",
        },
        soloAndPro: {
          available: true,
          creditCost: "Free",
          limitation: "Unlimited",
        },
      },
      {
        title: "Discovery Feeds",
        description: "Get inspired in niches that matter to you",
        tooltip: "View content from curated RSS feeds or bring your own feeds and topics. Refreshes every 30 minutes.",
        free: {
          available: true,
          creditCost: "Free",
          limitation: "Curated feeds only",
        },
        weekly: {
          available: true,
          creditCost: "Free",
          limitation: "1 Custom Feed",
        },
        soloAndPro: {
          available: true,
          creditCost: "Free",
          limitation: "Unlimited",
        },
      },
    ],
  },
  {
    groupName: "AI Analysis & Feedback",
    features: [
      {
        title: "Writing Styles & Brand Profiles",
        description: "Create unique writing profiles for your writing",
        tooltip: "Arcitext analyzes your existing text, and extracts a detailed writing DNA. You can adjust it as you please.",
        free: {
          available: true,
          creditCost: "4 credits to create",
          limitation: "1 Writing Style",
        },
        weekly: {
          available: true,
          creditCost: "4 credits to create",
          limitation: "1 Writing Style",
        },
        soloAndPro: {
          available: true,
          creditCost: "4 credits to create",
          limitation: "Unlimited",
        },
      },
      {
        title: "Tone Fit",
        description: "Get tone alignment feedback and suggestions",
        tooltip: "Highlight any text to get tone alignment scores and feedback for improvement",
        free: {
          available: true,
          creditCost: "1 credit",
        },
        weekly: {
          available: true,
          creditCost: "1 credit",
        },
        soloAndPro: {
          available: true,
          creditCost: "1 credit",
        },
      },
      {
        title: "Rewrite suggestions",
        description: "Ask Arcitext to suggest rewrites of any sentence",
        tooltip: "Select a sentence, click 'Rewrite' and Arcitext will suggest various rewrites that match your context",
        free: {
          available: true,
          creditCost: "1 credit",
        },
        weekly: {
          available: true,
          creditCost: "1 credit",
        },
        soloAndPro: {
          available: true,
          creditCost: "1 credit",
        },
      },
      {
        title: "Fact Checking",
        description: "Verify claims with credible sources",
        tooltip: "Arcitext fact-checks any claim on-demand, and provides sources where you can read more.",
        free: {
          available: true,
          creditCost: "5 credits",
        },
        weekly: {
          available: true,
          creditCost: "5 credits",
        },
        soloAndPro: {
          available: true,
          creditCost: "5 credits",
        },
      },
      {
        title: "Full Document Analyzis",
        description: "Get detailed feedback and improvements on your text",
        tooltip: "Arcitext analyzes your text and provides detailed scores for brand voice, purpose fit, clarity, and much more",
        free: {
          available: true,
          creditCost: "Variable",
        },
        weekly: {
          available: true,
          creditCost: "Variable",
        },
        soloAndPro: {
          available: true,
          creditCost: "Variable",
        },
      },
      {
        title: "SEO Optimization",
        description: "Keyword analyzis and optimization",
        tooltip: "Get keyword density analyzis, readability scores, and SEO recommendations",
        free: {
          available: true,
          creditCost: "Part of Full Document Analyzis",
        },
        weekly: {
          available: true,
          creditCost: "Part of Full Document Analyzis",
        },
        soloAndPro: {
          available: true,
          creditCost: "Part of Full Document Analyzis",
        },
      },
    ],
  },
  {
    groupName: "Content Creation",
    features: [
      {
        title: "Idea Generation",
        description: "Get the words flowing or upsell new ideas",
        tooltip: "Arcitext generates relevant content ideas from articles, based on your writing style, audience, purpose and channel.",
        free: {
          available: true,
          creditCost: "1 credit",
        },
        weekly: {
          available: true,
          creditCost: "1 credit",
        },
        soloAndPro: {
          available: true,
          creditCost: "1 credit",
        },
      },
      {
        title: "Content Repurpose",
        description: "Adapt content for multiple channels",
        tooltip: "Transform your content for different formats (social media, email, blog posts, etc.)",
        free: {
          available: false,
        },
        weekly: {
          available: true,
          creditCost: "10 credits",
        },
        soloAndPro: {
          available: true,
          creditCost: "10 credits",
        },
      },
    ],
  },
  {
    groupName: "Collaboration",
    features: [
      {
        title: "Collaboration & Sharing",
        description: "Share content with external reviewers",
        tooltip: "Generate share links for stakeholders to review content without account access",
        comingSoon: true,
        free: {
          available: false,
        },
        weekly: {
          available: false,
        },
        soloAndPro: {
          available: true,
        },
      },
    ],
  },
];

export function FeatureComparisonTable() {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="hidden md:table-cell text-left py-4 px-4 font-semibold text-foreground">
              Feature
            </th>
            <th className="text-center py-4 px-4 font-semibold text-foreground min-w-[160px]">
              Burst
            </th>
            <th className="text-center py-4 px-4 font-semibold text-foreground min-w-[160px]">
              Solo
            </th>
          </tr>
        </thead>
        <tbody>
          {featureGroups.map((group, groupIndex) => (
            <React.Fragment key={`group-${groupIndex}`}>
              {/* Group Header Row */}
              <tr className="hidden md:table-row">
                <td colSpan={3} className="py-3 px-4 bg-muted/50">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">
                    {group.groupName}
                  </h3>
                </td>
              </tr>

              {/* Mobile Group Header */}
              <tr className="md:hidden">
                <td colSpan={3} className="py-3 px-4 bg-muted/50">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider">
                    {group.groupName}
                  </h3>
                </td>
              </tr>

              {/* Features in this group */}
              {group.features.map((feature, featureIndex) => (
                <React.Fragment key={`feature-${groupIndex}-${featureIndex}`}>
                  {/* Mobile: Feature Name Row (full width) */}
                  <tr className="md:hidden border-b border-border/50">
                    <td colSpan={3} className="sticky left-0 py-4 px-4 bg-background">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-foreground">
                              {feature.title}
                            </span>
                            {feature.comingSoon && (
                              <Badge variant="secondary" className="text-xs">
                                Coming Soon
                              </Badge>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{feature.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>

                  {/* Desktop & Mobile: Feature Data Row */}
                  <tr className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    {/* Feature Column - Desktop Only */}
                    <td className="hidden md:table-cell py-4 px-4">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="font-medium text-foreground">
                              {feature.title}
                            </span>
                            {feature.comingSoon && (
                              <Badge variant="secondary" className="text-xs">
                                Coming Soon
                              </Badge>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>{feature.tooltip}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Weekly Column */}
                    <td className="py-4 px-4">
                      <div className="flex flex-col items-center gap-2 text-center">
                        {feature.weekly.available ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50" />
                        )}
                        {feature.weekly.creditCost && (
                          <span className="text-sm font-medium text-foreground">
                            {feature.weekly.creditCost}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Solo Column */}
                    <td className="py-4 px-4">
                      <div className="flex flex-col items-center gap-2 text-center">
                        {feature.soloAndPro.available ? (
                          <Check className="h-5 w-5 text-green-500" />
                        ) : (
                          <X className="h-5 w-5 text-muted-foreground/50" />
                        )}
                        {feature.soloAndPro.creditCost && (
                          <span className="text-sm font-medium text-foreground">
                            {feature.soloAndPro.creditCost}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
